import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Phone, PhoneOff, PhoneIncoming, Wifi, WifiOff,
  Loader2, AlertCircle, RefreshCw, Clock, User
} from 'lucide-react';
import Softphone from '../components/voip/Softphone';
import CallLog from '../components/voip/CallLog';
import { useAuth } from '../context/AuthContext';
import { voipService } from '../services/voipService';

/**
 * PhonePopout — Compact phone-only page for the Virgo Browser floating popup.
 * No sidebar, no header. Dark theme, optimized for 380x600.
 */
const PhonePopout = () => {
  const { user, loading: authLoading } = useAuth();
  const [extension, setExtension] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dialer');
  const [error, setError] = useState(null);

  // Lifted phone state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callState, setCallState] = useState('idle');
  const [connectionState, setConnectionState] = useState('disconnected');
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCallInfo, setIncomingCallInfo] = useState(null);

  useEffect(() => {
    if (!user) return;

    const loadExtension = async () => {
      try {
        const response = await voipService.getMyExtension();
        setExtension(response.data || response);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load extension:', err);
        setError('Could not load phone extension');
        setLoading(false);
      }
    };

    loadExtension();
  }, [user]);

  // Auth loading
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-virgo-navy-900">
        <Loader2 className="w-8 h-8 text-virgo-teal-400 animate-spin" />
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Extension loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-virgo-navy-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-virgo-teal-400 animate-spin mx-auto mb-3" />
          <p className="text-virgo-teal-400 text-sm">Connecting phone...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-virgo-navy-900">
        <div className="text-center p-4">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-red-300 text-sm mb-3">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 bg-cyan-600 text-white text-sm rounded-lg flex items-center gap-1.5 mx-auto"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dialer', label: 'Dialer', icon: Phone },
    { id: 'recent', label: 'Recent', icon: Clock },
  ];

  return (
    <div className="h-screen flex flex-col bg-virgo-navy-900 text-white overflow-hidden select-none">
      {/* Compact header */}
      <div className="flex items-center justify-between px-3 py-2 bg-virgo-navy-800 border-b border-virgo-navy-700">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-virgo-teal-400" />
          <span className="text-sm font-semibold text-white">Planchette</span>
        </div>
        <div className="flex items-center gap-2">
          {connectionState === 'connected' ? (
            <Wifi className="w-3.5 h-3.5 text-green-400" />
          ) : (
            <WifiOff className="w-3.5 h-3.5 text-red-400" />
          )}
          {extension && (
            <span className="text-xs text-virgo-teal-400/70">
              Ext {extension.extension_number || extension.id}
            </span>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex bg-virgo-navy-800 border-b border-virgo-navy-700">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-virgo-teal-400 border-b-2 border-virgo-teal-400 bg-virgo-navy-900/50'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'dialer' && (
          <div className="h-full overflow-y-auto">
            <Softphone
              extension={extension}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              callState={callState}
              setCallState={setCallState}
              connectionState={connectionState}
              setConnectionState={setConnectionState}
              activeCall={activeCall}
              setActiveCall={setActiveCall}
              incomingCallInfo={incomingCallInfo}
              setIncomingCallInfo={setIncomingCallInfo}
            />
          </div>
        )}
        {activeTab === 'recent' && (
          <div className="h-full overflow-y-auto p-2">
            <CallLog compact={true} />
          </div>
        )}
      </div>

      {/* Status bar */}
      {callState !== 'idle' && (
        <div className={`px-3 py-1.5 text-xs font-medium text-center ${
          callState === 'active' ? 'bg-green-600/20 text-green-400' :
          callState === 'incoming' ? 'bg-yellow-600/20 text-yellow-400 animate-pulse' :
          'bg-cyan-600/20 text-cyan-400'
        }`}>
          {callState === 'active' && 'Call in progress'}
          {callState === 'incoming' && 'Incoming call...'}
          {callState === 'calling' && 'Dialing...'}
          {callState === 'ringing' && 'Ringing...'}
          {callState === 'hold' && 'On hold'}
        </div>
      )}
    </div>
  );
};

export default PhonePopout;
